// UltraFlex Admin — Ad-hoc Report Builder
// Allows non-SQL users to build, run, save and share custom reports

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Database, Play, Save, Download, Trash2, Plus, X, ChevronDown,
  Folder, Share2, ArrowUpDown, Filter, Columns, BarChart3, Layers,
  FileText, RefreshCw, Copy, Eye, Settings2, BookOpen,
  Clock, Mail, ToggleLeft, ToggleRight, Send, Calendar, Pause, CheckCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OPERATORS = [
  { id: 'equals', label: 'Equals', types: ['string', 'number', 'date', 'datetime', 'boolean'] },
  { id: 'not_equals', label: 'Not Equals', types: ['string', 'number', 'date', 'datetime'] },
  { id: 'contains', label: 'Contains', types: ['string'] },
  { id: 'starts_with', label: 'Starts With', types: ['string'] },
  { id: 'greater_than', label: 'Greater Than', types: ['number', 'date', 'datetime'] },
  { id: 'less_than', label: 'Less Than', types: ['number', 'date', 'datetime'] },
  { id: 'greater_or_equal', label: '>=', types: ['number', 'date', 'datetime'] },
  { id: 'less_or_equal', label: '<=', types: ['number', 'date', 'datetime'] },
  { id: 'is_null', label: 'Is Empty', types: ['string', 'number', 'date', 'datetime'] },
  { id: 'is_not_null', label: 'Is Not Empty', types: ['string', 'number', 'date', 'datetime'] },
];

const AGG_FUNCTIONS = [
  { id: 'count', label: 'Count' },
  { id: 'count_distinct', label: 'Count Distinct' },
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Average' },
  { id: 'min', label: 'Min' },
  { id: 'max', label: 'Max' },
];

const AdminReportBuilder = () => {
  const { token, user } = useAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Data sources
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [joinSource, setJoinSource] = useState('');
  const [sourceColumns, setSourceColumns] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Report definition
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [sortBy, setSortBy] = useState([]);
  const [limitRows, setLimitRows] = useState(500);

  // Results
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Saved reports
  const [savedReports, setSavedReports] = useState([]);
  const [activeReportId, setActiveReportId] = useState(null);
  const [reportName, setReportName] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  // Active builder section
  const [activeSection, setActiveSection] = useState('columns');

  // Schedules
  const [schedules, setSchedules] = useState([]);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'weekly', day_of_week: 0, day_of_month: 1, hour: 7, minute: 0, recipients: '', is_active: true,
  });
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Load data sources and templates
  useEffect(() => {
    fetch(`${API_URL}/api/report-builder/sources`, { headers })
      .then(r => r.json())
      .then(d => setSources(d.sources || []))
      .catch(() => {});
    fetch(`${API_URL}/api/report-builder/templates`, { headers })
      .then(r => r.json())
      .then(d => setTemplates(d.templates || []))
      .catch(() => {});
    loadSavedReports();
    loadSchedules();
  }, []);

  const loadSavedReports = () => {
    fetch(`${API_URL}/api/report-builder/saved`, { headers })
      .then(r => r.json())
      .then(d => setSavedReports(d.items || []))
      .catch(() => {});
  };

  const loadSchedules = () => {
    fetch(`${API_URL}/api/report-builder/schedules`, { headers })
      .then(r => r.json())
      .then(d => setSchedules(d.items || []))
      .catch(() => {});
  };

  const openScheduleModal = (reportId) => {
    setScheduleForm({ frequency: 'weekly', day_of_week: 0, day_of_month: 1, hour: 7, minute: 0, recipients: '', is_active: true });
    setEditingScheduleId(null);
    setShowScheduleModal(true);
  };

  const editSchedule = (sched) => {
    setScheduleForm({
      frequency: sched.frequency,
      day_of_week: sched.day_of_week ?? 0,
      day_of_month: sched.day_of_month ?? 1,
      hour: sched.hour ?? 7,
      minute: sched.minute ?? 0,
      recipients: (sched.recipients || []).join(', '),
      is_active: sched.is_active,
    });
    setEditingScheduleId(sched.id);
    // Load the associated report
    const report = savedReports.find(r => r.id === sched.saved_report_id);
    if (report) loadReport(report);
    setShowScheduleModal(true);
  };

  const saveSchedule = async () => {
    if (!activeReportId) return;
    setScheduleLoading(true);
    const body = {
      saved_report_id: activeReportId,
      frequency: scheduleForm.frequency,
      day_of_week: scheduleForm.frequency === 'weekly' ? scheduleForm.day_of_week : null,
      day_of_month: scheduleForm.frequency === 'monthly' ? scheduleForm.day_of_month : null,
      hour: scheduleForm.hour,
      minute: scheduleForm.minute,
      recipients: scheduleForm.recipients,
      is_active: scheduleForm.is_active,
    };
    try {
      let res;
      if (editingScheduleId) {
        res = await fetch(`${API_URL}/api/report-builder/schedules/${editingScheduleId}`, { method: 'PATCH', headers, body: JSON.stringify(body) });
      } else {
        res = await fetch(`${API_URL}/api/report-builder/schedules`, { method: 'POST', headers, body: JSON.stringify(body) });
      }
      if (res.ok) {
        loadSchedules();
        setShowScheduleModal(false);
      }
    } catch {}
    setScheduleLoading(false);
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    await fetch(`${API_URL}/api/report-builder/schedules/${id}`, { method: 'DELETE', headers });
    loadSchedules();
  };

  const toggleSchedule = async (id) => {
    await fetch(`${API_URL}/api/report-builder/schedules/${id}/toggle`, { method: 'POST', headers });
    loadSchedules();
  };

  const runScheduleNow = async (id) => {
    const res = await fetch(`${API_URL}/api/report-builder/schedules/${id}/run-now`, { method: 'POST', headers });
    if (res.ok) {
      const data = await res.json();
      alert(`Run complete: ${data.status} — ${data.message || ''}`);
      loadSchedules();
    }
  };

  // When source or join changes, update available columns
  useEffect(() => {
    if (selectedSource) {
      const src = sources.find(s => s.id === selectedSource);
      let cols = (src?.columns || []).map(c => ({ ...c, id: `${selectedSource}.${c.id}`, source: selectedSource }));
      if (joinSource) {
        const jSrc = sources.find(s => s.id === joinSource);
        const jCols = (jSrc?.columns || []).map(c => ({ ...c, id: `${joinSource}.${c.id}`, source: joinSource, label: `${jSrc?.label}: ${c.label}` }));
        cols = [...cols, ...jCols];
      }
      setSourceColumns(cols);
      if (!activeReportId) {
        setSelectedColumns(cols.slice(0, 8).map(c => c.id));
        setFilters([]);
        setGroupBy([]);
        setAggregations([]);
        setSortBy([]);
        setResults(null);
      }
    }
  }, [selectedSource, joinSource, sources]);

  const buildReportDef = () => ({
    data_source: selectedSource,
    join_source: joinSource || undefined,
    columns: selectedColumns,
    filters,
    group_by: groupBy,
    aggregations,
    sort_by: sortBy,
    limit_rows: limitRows,
  });

  const runReport = async () => {
    if (!selectedSource) { setError('Select a data source first'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/report-builder/execute`, {
        method: 'POST', headers, body: JSON.stringify(buildReportDef()),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Query failed');
      setResults(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const exportCSV = async () => {
    if (!selectedSource) return;
    try {
      const res = await fetch(`${API_URL}/api/report-builder/export-csv`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...buildReportDef(), name: reportName || 'report' }),
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(reportName || 'report').replace(/\s+/g, '_').toLowerCase()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {}
  };

  const saveReport = async () => {
    if (!reportName.trim() || !selectedSource) return;
    try {
      const body = { ...buildReportDef(), name: reportName, description: reportDesc, is_shared: isShared };
      let res;
      if (activeReportId) {
        res = await fetch(`${API_URL}/api/report-builder/saved/${activeReportId}`, { method: 'PATCH', headers, body: JSON.stringify(body) });
      } else {
        res = await fetch(`${API_URL}/api/report-builder/saved`, { method: 'POST', headers, body: JSON.stringify(body) });
      }
      if (res.ok) {
        const saved = await res.json();
        setActiveReportId(saved.id);
        loadSavedReports();
        setShowSaveModal(false);
      }
    } catch {}
  };

  const loadReport = (report) => {
    setSelectedSource(report.data_source);
    setJoinSource(report.join_source || '');
    const src = sources.find(s => s.id === report.data_source);
    let cols = (src?.columns || []).map(c => ({ ...c, id: `${report.data_source}.${c.id}`, source: report.data_source }));
    if (report.join_source) {
      const jSrc = sources.find(s => s.id === report.join_source);
      const jCols = (jSrc?.columns || []).map(c => ({ ...c, id: `${report.join_source}.${c.id}`, source: report.join_source, label: `${jSrc?.label}: ${c.label}` }));
      cols = [...cols, ...jCols];
    }
    setSourceColumns(cols);
    setSelectedColumns(report.columns || []);
    setFilters(report.filters || []);
    setGroupBy(report.group_by || []);
    setAggregations(report.aggregations || []);
    setSortBy(report.sort_by || []);
    setLimitRows(report.limit_rows || 500);
    setReportName(report.name);
    setReportDesc(report.description || '');
    setIsShared(report.is_shared);
    setActiveReportId(report.id);
    setShowSaved(false);
    setResults(null);
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this saved report?')) return;
    await fetch(`${API_URL}/api/report-builder/saved/${id}`, { method: 'DELETE', headers });
    if (activeReportId === id) resetBuilder();
    loadSavedReports();
  };

  const resetBuilder = () => {
    setSelectedSource('');
    setJoinSource('');
    setSourceColumns([]);
    setSelectedColumns([]);
    setFilters([]);
    setGroupBy([]);
    setAggregations([]);
    setSortBy([]);
    setResults(null);
    setReportName('');
    setReportDesc('');
    setActiveReportId(null);
    setIsShared(false);
    setError('');
    setLimitRows(500);
  };

  const duplicateReport = (report) => {
    loadReport({ ...report, id: null });
    setActiveReportId(null);
    setReportName(`${report.name} (Copy)`);
  };

  const toggleColumn = (colId) => {
    setSelectedColumns(prev =>
      prev.includes(colId) ? prev.filter(c => c !== colId) : [...prev, colId]
    );
  };

  const addFilter = () => setFilters([...filters, { column: sourceColumns[0]?.id || '', operator: 'equals', value: '' }]);
  const removeFilter = (i) => setFilters(filters.filter((_, idx) => idx !== i));
  const updateFilter = (i, field, val) => setFilters(filters.map((f, idx) => idx === i ? { ...f, [field]: val } : f));

  const addAgg = () => setAggregations([...aggregations, { function: 'count', column: 'id', label: '' }]);
  const removeAgg = (i) => setAggregations(aggregations.filter((_, idx) => idx !== i));
  const updateAgg = (i, field, val) => {
    const newAggs = aggregations.map((a, idx) => idx === i ? { ...a, [field]: val } : a);
    if (field === 'function' || field === 'column') {
      const a = newAggs[i];
      a.label = `${a.function}(${a.column})`;
    }
    setAggregations(newAggs);
  };

  const addSort = () => setSortBy([...sortBy, { column: sourceColumns[0]?.id || '', direction: 'asc' }]);
  const removeSort = (i) => setSortBy(sortBy.filter((_, idx) => idx !== i));

  const operatorsFor = (colId) => {
    const colType = sourceColumns.find(c => c.id === colId)?.type || 'string';
    return OPERATORS.filter(o => o.types.includes(colType));
  };

  const myReports = savedReports.filter(r => r.owner_id === user?.id);
  const sharedReports = savedReports.filter(r => r.is_shared && r.owner_id !== user?.id);

  const sectionTabs = [
    { id: 'columns', label: 'Columns', icon: Columns, count: selectedColumns.length },
    { id: 'filters', label: 'Filters', icon: Filter, count: filters.length },
    { id: 'grouping', label: 'Group & Aggregate', icon: Layers, count: groupBy.length + aggregations.length },
    { id: 'sorting', label: 'Sort', icon: ArrowUpDown, count: sortBy.length },
  ];

  return (
    <div className="p-4 lg:p-8" data-testid="report-builder-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text">Report Builder</h1>
          <p className="text-uf-text-muted text-sm mt-1">Build custom reports without SQL</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowSchedules(!showSchedules)} data-testid="toggle-schedules"
            className="flex items-center gap-2 px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm hover:bg-uf-surface-hi transition-colors">
            <Clock className="w-4 h-4" />Schedules
            {schedules.length > 0 && <span className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{schedules.length}</span>}
          </button>
          <button onClick={() => setShowSaved(!showSaved)} data-testid="toggle-saved-reports"
            className="flex items-center gap-2 px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm hover:bg-uf-surface-hi transition-colors">
            <BookOpen className="w-4 h-4" />Saved Reports
            {savedReports.length > 0 && <span className="bg-uf-red text-white text-[10px] px-1.5 py-0.5 rounded-full">{savedReports.length}</span>}
          </button>
          <button onClick={() => { resetBuilder(); setShowSaved(false); setShowSchedules(false); setError(''); }}
            className="flex items-center gap-2 px-4 py-2 bg-uf-red text-white rounded-lg text-sm hover:bg-uf-red/90 transition-colors" data-testid="new-report-btn">
            <Plus className="w-4 h-4" />New Report
          </button>
        </div>
      </div>

      {/* Saved Reports Panel */}
      {showSaved && (
        <div className="mb-6 bg-uf-surface rounded-xl border border-uf-border p-5" data-testid="saved-reports-panel">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-uf-text">Saved Reports</h3>
            <button onClick={() => setShowSaved(false)} className="text-uf-text-muted hover:text-uf-text"><X className="w-4 h-4" /></button>
          </div>

          {myReports.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-uf-text-muted uppercase tracking-wider mb-2">My Reports</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {myReports.map(r => (
                  <ReportCard key={r.id} report={r} onLoad={loadReport} onDelete={deleteReport} onDuplicate={duplicateReport} active={activeReportId === r.id} />
                ))}
              </div>
            </div>
          )}

          {sharedReports.length > 0 && (
            <div>
              <p className="text-xs text-uf-text-muted uppercase tracking-wider mb-2">Shared Repository</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {sharedReports.map(r => (
                  <ReportCard key={r.id} report={r} onLoad={loadReport} onDuplicate={duplicateReport} active={activeReportId === r.id} isOther />
                ))}
              </div>
            </div>
          )}

          {myReports.length === 0 && sharedReports.length === 0 && (
            <p className="text-uf-text-muted text-sm text-center py-4">No saved reports yet. Build and save your first report.</p>
          )}
        </div>
      )}

      {/* Schedules Panel */}
      {showSchedules && (
        <div className="mb-6 bg-uf-surface rounded-xl border border-uf-border p-5" data-testid="schedules-panel">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-uf-text">Automated Schedules</h3>
            <button onClick={() => setShowSchedules(false)} className="text-uf-text-muted hover:text-uf-text"><X className="w-4 h-4" /></button>
          </div>

          {schedules.length > 0 ? (
            <div className="space-y-3">
              {schedules.map(sched => (
                <ScheduleCard key={sched.id} schedule={sched}
                  onEdit={editSchedule} onDelete={deleteSchedule} onToggle={toggleSchedule} onRunNow={runScheduleNow} />
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Clock className="w-10 h-10 mx-auto mb-3 text-uf-text-muted opacity-40" />
              <p className="text-uf-text-muted text-sm">No automated schedules yet.</p>
              <p className="text-uf-text-muted text-xs mt-1">Save a report first, then schedule it for automatic delivery.</p>
            </div>
          )}
        </div>
      )}

      {/* Data Source Selection */}
      <div className="mb-4">
        <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-2 block">Primary Data Source</label>
        <div className="flex flex-wrap gap-2">
          {sources.map(src => (
            <button key={src.id} onClick={() => { setSelectedSource(src.id); setJoinSource(''); setActiveReportId(null); setResults(null); }}
              data-testid={`source-${src.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                selectedSource === src.id
                  ? 'bg-uf-red text-white shadow-lg shadow-red-900/20'
                  : 'bg-uf-surface border border-uf-border text-uf-text-muted hover:text-uf-text hover:border-uf-red/40'
              }`}>
              <Database className="w-3.5 h-3.5" />{src.label}
            </button>
          ))}
        </div>
      </div>

      {/* Join Source Selection */}
      {selectedSource && (() => {
        const src = sources.find(s => s.id === selectedSource);
        const joinable = src?.joinable_sources || [];
        return joinable.length > 0 && (
          <div className="mb-4" data-testid="join-source-section">
            <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-2 block">
              Join With (optional — combine related data)
            </label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setJoinSource('')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  !joinSource ? 'bg-uf-surface-hi text-uf-text border border-uf-border' : 'text-uf-text-muted hover:text-uf-text'
                }`}>None</button>
              {joinable.map(j => (
                <button key={j.source} onClick={() => setJoinSource(j.source)} data-testid={`join-${j.source}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    joinSource === j.source
                      ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                      : 'bg-uf-surface border border-uf-border text-uf-text-muted hover:text-uf-text hover:border-blue-500/30'
                  }`}>
                  <Layers className="w-3 h-3" />{j.label}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Smart Templates */}
      {!selectedSource && templates.length > 0 && (
        <div className="mb-6" data-testid="report-templates">
          <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-3 block">Smart Report Templates</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(t => (
              <button key={t.id} onClick={() => {
                setSelectedSource(t.data_source);
                setJoinSource(t.join_source || '');
                setSelectedColumns(t.columns || []);
                setFilters(t.filters || []);
                setGroupBy(t.group_by || []);
                setAggregations(t.aggregations || []);
                setActiveReportId(null);
                setReportName(t.name);
              }}
              data-testid={`template-${t.id}`}
              className="bg-uf-surface border border-uf-border rounded-xl p-4 text-left hover:border-uf-red/40 transition-all group">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-uf-red group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-semibold text-uf-text">{t.name}</span>
                </div>
                <p className="text-uf-text-muted text-xs">{t.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedSource && (
        <>
          {/* Builder Sections */}
          <div className="flex gap-1 mb-4 bg-uf-surface rounded-lg p-1 overflow-x-auto">
            {sectionTabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSection === tab.id ? 'bg-uf-surface-hi text-uf-text' : 'text-uf-text-muted hover:text-uf-text'
                }`}>
                <tab.icon className="w-4 h-4" />{tab.label}
                {tab.count > 0 && <span className="bg-uf-red/20 text-uf-red text-[10px] px-1.5 py-0.5 rounded-full">{tab.count}</span>}
              </button>
            ))}
          </div>

          {/* Column Selection */}
          {activeSection === 'columns' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4" data-testid="column-picker">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-uf-text">Select Columns</h3>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedColumns(sourceColumns.map(c => c.id))} className="text-xs text-uf-text-muted hover:text-uf-red">Select All</button>
                  <button onClick={() => setSelectedColumns([])} className="text-xs text-uf-text-muted hover:text-uf-red">Clear</button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {sourceColumns.map(col => (
                  <label key={col.id} className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer transition-all ${
                    selectedColumns.includes(col.id) ? 'bg-uf-red/10 border border-uf-red/30' : 'bg-uf-surface-hi border border-transparent hover:border-uf-border'
                  }`}>
                    <input type="checkbox" checked={selectedColumns.includes(col.id)} onChange={() => toggleColumn(col.id)}
                      className="accent-red-500" />
                    <span className="text-sm text-uf-text">{col.label}</span>
                    <span className="text-[10px] text-uf-text-muted ml-auto">{col.type}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          {activeSection === 'filters' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4" data-testid="filter-builder">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-uf-text">Filters</h3>
                <button onClick={addFilter} className="flex items-center gap-1 text-xs text-uf-red hover:underline" data-testid="add-filter-btn">
                  <Plus className="w-3 h-3" />Add Filter
                </button>
              </div>
              {filters.length === 0 && <p className="text-uf-text-muted text-sm">No filters. All rows will be returned.</p>}
              <div className="space-y-2">
                {filters.map((f, i) => (
                  <div key={i} className="flex flex-wrap gap-2 items-center bg-uf-surface-hi rounded-lg p-3">
                    <select value={f.column} onChange={e => updateFilter(i, 'column', e.target.value)}
                      className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text">
                      {sourceColumns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <select value={f.operator} onChange={e => updateFilter(i, 'operator', e.target.value)}
                      className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text">
                      {operatorsFor(f.column).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                    {!['is_null', 'is_not_null'].includes(f.operator) && (
                      <input value={f.value} onChange={e => updateFilter(i, 'value', e.target.value)}
                        placeholder="Value..."
                        className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text flex-1 min-w-[120px]" />
                    )}
                    <button onClick={() => removeFilter(i)} className="text-uf-text-muted hover:text-red-400 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grouping & Aggregation */}
          {activeSection === 'grouping' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4" data-testid="grouping-builder">
              <h3 className="text-sm font-semibold text-uf-text mb-3">Group By</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {sourceColumns.filter(c => ['string', 'date', 'boolean'].includes(c.type)).map(col => (
                  <button key={col.id} onClick={() => setGroupBy(prev => prev.includes(col.id) ? prev.filter(g => g !== col.id) : [...prev, col.id])}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      groupBy.includes(col.id) ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-uf-surface-hi text-uf-text-muted border border-transparent hover:border-uf-border'
                    }`}>
                    {col.label}
                  </button>
                ))}
              </div>

              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-uf-text">Aggregations</h3>
                <button onClick={addAgg} className="flex items-center gap-1 text-xs text-uf-red hover:underline" data-testid="add-agg-btn">
                  <Plus className="w-3 h-3" />Add Measure
                </button>
              </div>
              {aggregations.length === 0 && <p className="text-uf-text-muted text-sm">Add measures to calculate totals, averages, etc.</p>}
              <div className="space-y-2">
                {aggregations.map((a, i) => (
                  <div key={i} className="flex gap-2 items-center bg-uf-surface-hi rounded-lg p-3">
                    <select value={a.function} onChange={e => updateAgg(i, 'function', e.target.value)}
                      className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text">
                      {AGG_FUNCTIONS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                    <span className="text-uf-text-muted text-sm">of</span>
                    <select value={a.column} onChange={e => updateAgg(i, 'column', e.target.value)}
                      className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text flex-1">
                      {sourceColumns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <button onClick={() => removeAgg(i)} className="text-uf-text-muted hover:text-red-400 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sorting */}
          {activeSection === 'sorting' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4" data-testid="sort-builder">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-uf-text">Sort Order</h3>
                <button onClick={addSort} className="flex items-center gap-1 text-xs text-uf-red hover:underline">
                  <Plus className="w-3 h-3" />Add Sort
                </button>
              </div>
              {sortBy.length === 0 && <p className="text-uf-text-muted text-sm">No sorting. Default order will be used.</p>}
              <div className="space-y-2">
                {sortBy.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center bg-uf-surface-hi rounded-lg p-3">
                    <select value={s.column} onChange={e => setSortBy(sortBy.map((ss, idx) => idx === i ? { ...ss, column: e.target.value } : ss))}
                      className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text flex-1">
                      {sourceColumns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <select value={s.direction} onChange={e => setSortBy(sortBy.map((ss, idx) => idx === i ? { ...ss, direction: e.target.value } : ss))}
                      className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text">
                      <option value="asc">A-Z / Low-High</option>
                      <option value="desc">Z-A / High-Low</option>
                    </select>
                    <button onClick={() => removeSort(i)} className="text-uf-text-muted hover:text-red-400 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <label className="text-sm text-uf-text-muted">Max rows:</label>
                <input type="number" value={limitRows} onChange={e => setLimitRows(Math.min(Number(e.target.value) || 500, 5000))}
                  className="bg-uf-bg border border-uf-border rounded-lg px-3 py-1.5 text-sm text-uf-text w-24" min={1} max={5000} />
              </div>
            </div>
          )}

          {/* Action Bar */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button onClick={runReport} disabled={loading} data-testid="run-report-btn"
              className="flex items-center gap-2 px-6 py-2.5 bg-uf-red text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {loading ? 'Running...' : 'Run Report'}
            </button>
            <button onClick={() => setShowSaveModal(true)} data-testid="save-report-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm hover:bg-uf-surface-hi transition-colors">
              <Save className="w-4 h-4" />{activeReportId ? 'Update' : 'Save'}
            </button>
            {results && (
              <button onClick={exportCSV} data-testid="export-csv-btn"
                className="flex items-center gap-2 px-4 py-2.5 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm hover:bg-uf-surface-hi transition-colors">
                <Download className="w-4 h-4" />Export CSV
              </button>
            )}
            {activeReportId && (
              <button onClick={() => openScheduleModal(activeReportId)} data-testid="schedule-report-btn"
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600/10 border border-blue-500/30 rounded-lg text-blue-400 text-sm hover:bg-blue-600/20 transition-colors">
                <Clock className="w-4 h-4" />Schedule
              </button>
            )}
            {activeReportId && <span className="flex items-center text-xs text-uf-text-muted self-center">{reportName}</span>}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">{error}</div>
          )}

          {/* Results Table */}
          {results && (
            <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid="report-results">
              <div className="px-5 py-3 border-b border-uf-border flex justify-between items-center">
                <h3 className="text-sm font-semibold text-uf-text">
                  Results <span className="text-uf-text-muted font-normal">({results.total} total{results.total > results.limited_to ? `, showing ${results.limited_to}` : ''})</span>
                </h3>
              </div>
              {results.rows?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-uf-surface-hi text-left text-uf-text-muted border-b border-uf-border">
                        <th className="px-4 py-2.5 text-xs">#</th>
                        {results.columns?.map(col => (
                          <th key={col.id} className="px-4 py-2.5 text-xs whitespace-nowrap">{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.rows.map((row, i) => (
                        <tr key={i} className="border-b border-uf-border/30 hover:bg-uf-surface-hi/50">
                          <td className="px-4 py-2 text-uf-text-muted text-xs">{i + 1}</td>
                          {results.columns?.map(col => (
                            <td key={col.id} className="px-4 py-2 text-uf-text text-xs whitespace-nowrap">
                              {formatCell(row[col.id], col.type)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 text-uf-text-muted opacity-40" />
                  <p className="text-uf-text-muted">No results match your criteria</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="schedule-modal">
          <div className="bg-uf-surface border border-uf-border rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-uf-text mb-1">{editingScheduleId ? 'Edit Schedule' : 'Schedule Report'}</h3>
            <p className="text-xs text-uf-text-muted mb-4">Automatically run &quot;{reportName}&quot; and email the CSV results</p>

            <div className="space-y-4">
              {/* Frequency */}
              <div>
                <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-2 block">Frequency</label>
                <div className="flex gap-2">
                  {['daily', 'weekly', 'monthly'].map(f => (
                    <button key={f} onClick={() => setScheduleForm(p => ({ ...p, frequency: f }))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        scheduleForm.frequency === f ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-uf-surface-hi text-uf-text-muted border border-transparent hover:border-uf-border'
                      }`}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
                  ))}
                </div>
              </div>

              {/* Day picker for weekly */}
              {scheduleForm.frequency === 'weekly' && (
                <div>
                  <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-2 block">Day of Week</label>
                  <div className="flex gap-1.5">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                      <button key={i} onClick={() => setScheduleForm(p => ({ ...p, day_of_week: i }))}
                        className={`w-10 h-10 rounded-lg text-xs font-medium transition-all ${
                          scheduleForm.day_of_week === i ? 'bg-blue-500 text-white' : 'bg-uf-surface-hi text-uf-text-muted hover:bg-uf-surface-hi/70'
                        }`}>{d}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Day picker for monthly */}
              {scheduleForm.frequency === 'monthly' && (
                <div>
                  <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-2 block">Day of Month</label>
                  <select value={scheduleForm.day_of_month} onChange={e => setScheduleForm(p => ({ ...p, day_of_month: Number(e.target.value) }))}
                    className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text">
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Time */}
              <div>
                <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-2 block">Time (UTC)</label>
                <div className="flex gap-2 items-center">
                  <select value={scheduleForm.hour} onChange={e => setScheduleForm(p => ({ ...p, hour: Number(e.target.value) }))}
                    className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text">
                    {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}</option>)}
                  </select>
                  <span className="text-uf-text-muted">:</span>
                  <select value={scheduleForm.minute} onChange={e => setScheduleForm(p => ({ ...p, minute: Number(e.target.value) }))}
                    className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text">
                    {[0, 15, 30, 45].map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                  </select>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-2 block">Recipients (comma-separated emails)</label>
                <input value={scheduleForm.recipients} onChange={e => setScheduleForm(p => ({ ...p, recipients: e.target.value }))}
                  placeholder="user1@example.com, user2@example.com"
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text" data-testid="schedule-recipients-input" />
              </div>

              {/* Active toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={scheduleForm.is_active} onChange={e => setScheduleForm(p => ({ ...p, is_active: e.target.checked }))} className="accent-blue-500" />
                <span className="text-sm text-uf-text">Active (starts running immediately)</span>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowScheduleModal(false)} className="flex-1 px-4 py-2 bg-uf-surface-hi border border-uf-border rounded-lg text-uf-text text-sm">Cancel</button>
              <button onClick={saveSchedule} disabled={scheduleLoading || !scheduleForm.recipients.trim()} data-testid="confirm-schedule-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                <Clock className="w-4 h-4" />{scheduleLoading ? 'Saving...' : (editingScheduleId ? 'Update Schedule' : 'Create Schedule')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="save-report-modal">
          <div className="bg-uf-surface border border-uf-border rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-uf-text mb-4">{activeReportId ? 'Update Report' : 'Save Report'}</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-1 block">Report Name *</label>
                <input value={reportName} onChange={e => setReportName(e.target.value)}
                  placeholder="e.g. Active Members by Site"
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text" data-testid="report-name-input" />
              </div>
              <div>
                <label className="text-xs text-uf-text-muted uppercase tracking-wider mb-1 block">Description</label>
                <textarea value={reportDesc} onChange={e => setReportDesc(e.target.value)}
                  placeholder="What does this report show?"
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text resize-none" rows={2} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isShared} onChange={e => setIsShared(e.target.checked)} className="accent-red-500" />
                <Share2 className="w-4 h-4 text-uf-text-muted" />
                <span className="text-sm text-uf-text">Share to central repository (visible to all staff)</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowSaveModal(false)} className="flex-1 px-4 py-2 bg-uf-surface-hi border border-uf-border rounded-lg text-uf-text text-sm">Cancel</button>
              <button onClick={saveReport} disabled={!reportName.trim()} data-testid="confirm-save-btn"
                className="flex-1 px-4 py-2 bg-uf-red text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {activeReportId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// === Sub-components ===

const ReportCard = ({ report, onLoad, onDelete, onDuplicate, active, isOther }) => (
  <div className={`p-3 rounded-lg border transition-all cursor-pointer ${
    active ? 'border-uf-red bg-uf-red/5' : 'border-uf-border bg-uf-surface-hi hover:border-uf-red/40'
  }`} onClick={() => onLoad(report)} data-testid={`saved-report-${report.id}`}>
    <div className="flex justify-between items-start">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-uf-text truncate">{report.name}</p>
        <p className="text-xs text-uf-text-muted mt-0.5">{report.data_source} {report.is_shared && '(shared)'}</p>
        {report.description && <p className="text-xs text-uf-text-muted mt-1 line-clamp-1">{report.description}</p>}
      </div>
      <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
        {report.is_shared && <Share2 className="w-3 h-3 text-blue-400" />}
        <button onClick={() => onDuplicate(report)} className="p-1 text-uf-text-muted hover:text-uf-text" title="Duplicate">
          <Copy className="w-3 h-3" />
        </button>
        {!isOther && onDelete && (
          <button onClick={() => onDelete(report.id)} className="p-1 text-uf-text-muted hover:text-red-400" title="Delete">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
    <p className="text-[10px] text-uf-text-muted mt-2">{isOther ? `by ${report.owner_name}` : ''} {report.updated_at ? new Date(report.updated_at).toLocaleDateString() : ''}</p>
  </div>
);

const formatCell = (value, type) => {
  if (value === null || value === undefined) return <span className="text-uf-text-muted">-</span>;
  if (type === 'number' && typeof value === 'number') return value.toLocaleString();
  if (type === 'boolean') return value ? 'Yes' : 'No';
  if (type === 'datetime' && typeof value === 'string') return new Date(value).toLocaleString();
  if (type === 'date' && typeof value === 'string') return new Date(value).toLocaleDateString();
  return String(value);
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ScheduleCard = ({ schedule, onEdit, onDelete, onToggle, onRunNow }) => {
  const statusIcon = schedule.last_run_status === 'success' ? <CheckCircle className="w-3.5 h-3.5 text-green-400" /> :
    schedule.last_run_status === 'failed' ? <AlertCircle className="w-3.5 h-3.5 text-red-400" /> :
    schedule.last_run_status === 'skipped' ? <Pause className="w-3.5 h-3.5 text-amber-400" /> : null;

  return (
    <div className={`p-4 rounded-lg border transition-all ${schedule.is_active ? 'border-blue-500/30 bg-blue-500/5' : 'border-uf-border bg-uf-surface-hi opacity-60'}`}
      data-testid={`schedule-${schedule.id}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-uf-text truncate">{schedule.report_name}</p>
            {!schedule.is_active && <span className="text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">PAUSED</span>}
          </div>
          <p className="text-xs text-blue-400 mt-1">{schedule.frequency_label}</p>
          <p className="text-xs text-uf-text-muted mt-1">
            <Mail className="w-3 h-3 inline mr-1" />{(schedule.recipients || []).join(', ')}
          </p>
        </div>
        <div className="flex gap-1 ml-3">
          <button onClick={() => onToggle(schedule.id)} className="p-1.5 text-uf-text-muted hover:text-blue-400" title={schedule.is_active ? 'Pause' : 'Activate'}>
            {schedule.is_active ? <ToggleRight className="w-4 h-4 text-blue-400" /> : <ToggleLeft className="w-4 h-4" />}
          </button>
          <button onClick={() => onRunNow(schedule.id)} className="p-1.5 text-uf-text-muted hover:text-green-400" title="Run Now">
            <Send className="w-4 h-4" />
          </button>
          <button onClick={() => onEdit(schedule)} className="p-1.5 text-uf-text-muted hover:text-uf-text" title="Edit">
            <Settings2 className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(schedule.id)} className="p-1.5 text-uf-text-muted hover:text-red-400" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px] text-uf-text-muted">
        {statusIcon}
        {schedule.last_run_at ? (
          <span>Last run: {new Date(schedule.last_run_at).toLocaleString()} — {schedule.last_run_message || schedule.last_run_status}</span>
        ) : (
          <span>Never run · {schedule.run_count} total runs</span>
        )}
      </div>
    </div>
  );
};

export default AdminReportBuilder;
